import { useQuery } from "@tanstack/react-query";
import { getApiProducts, type ProductResponse } from "apiClients/base-generated";
import { getHeyApiRequestOptions, normalizeHeyApiLang } from "apiClients/base/heyApiRequestOptions";
import type { SupplierProductListItem, SupplierProductsPage, SupplierQuickFilter } from "../types";

export const DEFAULT_SUPPLIER_PRODUCTS_PAGE_SIZE = 24;

export type SupplierProductsQueryParams = {
  q?: string;
  filter?: SupplierQuickFilter;
  groupId?: number | null;
  page?: number;
  pageSize?: number;
  locale?: string | null;
};

type ProductsPaginationResult = {
  data?: ProductResponse[] | null;
  total?: number;
};

function heyApiResultToError(err: unknown): Error {
  const message = typeof err === "string" ? err : JSON.stringify(err);
  return new Error(message);
}

function buildFilters(q: string, filter: SupplierQuickFilter): string | undefined {
  const search = q.trim();
  // Backend fullSearch returns early and cannot be combined with other Filters.
  if (search) {
    return `fullSearch_like=${search}`;
  }

  switch (filter) {
    case "published":
    case "has-material":
      return "IsPublished_eq=true";
    case "missing-material":
      return "IsPublished_eq=false";
    case "updated-30d": {
      const since = new Date();
      since.setDate(since.getDate() - 30);
      return `ModifiedAt_gt=${since.toISOString()}`;
    }
    default:
      return undefined;
  }
}

function mapProduct(product: ProductResponse): SupplierProductListItem {
  const imageCount = product.images?.length ?? 0;
  const docCount =
    (product.manuals?.length ?? 0) +
    (product.safetySheets?.length ?? 0) +
    (product.dimensionalSheets?.length ?? 0);
  const thumb =
    product.images?.[0]?.previewUrl || product.images?.[0]?.url || undefined;

  return {
    id: product.id ?? 0,
    name: product.name?.trim() || "Namnlös produkt",
    sku: product.mainItemNo?.trim() || "—",
    thumb,
    imageCount,
    docCount,
    published: Boolean(product.isPublished),
    modifiedAt: product.modifiedAt,
  };
}

export function useSupplierProductsQuery(params: SupplierProductsQueryParams = {}) {
  const q = params.q ?? "";
  const filter = params.filter ?? "all";
  const groupId = params.groupId ?? null;
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? DEFAULT_SUPPLIER_PRODUCTS_PAGE_SIZE;
  const lang = normalizeHeyApiLang(params.locale);

  return useQuery({
    queryKey: [
      "supplier-products",
      lang ?? "default",
      page,
      pageSize,
      q.trim(),
      filter,
      groupId ?? "all",
    ],
    queryFn: async (): Promise<SupplierProductsPage> => {
      const filters = buildFilters(q, filter);
      const result = await getApiProducts({
        ...getHeyApiRequestOptions(lang),
        query: {
          _start: (page - 1) * pageSize,
          _end: page * pageSize,
          _sort: "modifiedAt",
          _order: "desc",
          ...(groupId != null ? { productGroupId: groupId } : {}),
          ...(filters ? { Filters: filters } : {}),
        },
      });

      if (result.error) {
        throw heyApiResultToError(result.error);
      }

      const payload = (result.data ?? {}) as ProductsPaginationResult;
      const data = (payload.data ?? []).map(mapProduct);

      return {
        data,
        total: payload.total ?? data.length,
      };
    },
  });
}
